/** Old URL → new URL, in one place (plan 1-2).
 *
 *  A rule turns on in the Phase that builds its target page; until then the
 *  old page keeps its address and App.tsx mounts it. Nothing on the server
 *  links into the console (no Telegram or notice links in internal/ —
 *  checked 10-17), so the rules only have bookmarks to keep alive. Every rule
 *  keeps the query. */

import { Navigate, Route, matchPath, useLocation, useParams } from 'react-router-dom'

type Params = Record<string, string | undefined>

export interface RedirectRule {
  /** A path or a route pattern (`/apps/:id`), exactly as App.tsx mounts the
   *  old page. */
  from: string
  /** The Phase that builds the target page and turns this rule on. */
  phase: number
  active: boolean
  to: (query: URLSearchParams, params: Params) => string
}

function withQuery(path: string, q: URLSearchParams): string {
  const s = q.toString()
  return s ? `${path}?${s}` : path
}

export const REDIRECTS: RedirectRule[] = [
  // 오늘 takes the tracker's state and run button; the app cards go to the
  // /apps table, the change log to 변동 > 기록, the board to the keyword drawer.
  { from: '/tracking', phase: 3, active: true, to: (q) => withQuery('/', q) },
  // "analysis" (score) and "analytics" (usage) were one letter apart.
  { from: '/analysis', phase: 3, active: true, to: (q) => withQuery('/movement', q) },
  // The app's tabs become addresses; the bare one opens 키워드.
  {
    from: '/apps/:id',
    phase: 4,
    active: true,
    to: (q, p) => withQuery(`/apps/${encodeURIComponent(p.id ?? '')}/keywords`, q),
  },
]

/** Where an old URL goes. Ignores `active`, so tests can pin a rule before
 *  its Phase turns it on. Null when the path has no rule. */
export function resolveRedirect(pathname: string, search = ''): string | null {
  for (const rule of REDIRECTS) {
    const m = matchPath({ path: rule.from, end: true }, pathname)
    if (m) return rule.to(new URLSearchParams(search.startsWith('?') ? search.slice(1) : search), m.params)
  }
  return null
}

/** Paths whose old page has been replaced — App.tsx stops mounting them. */
export function isRedirected(path: string): boolean {
  return REDIRECTS.some((r) => r.active && r.from === path)
}

function RedirectTo({ rule }: { rule: RedirectRule }) {
  const { search } = useLocation()
  const params = useParams()
  return <Navigate replace to={rule.to(new URLSearchParams(search), params)} />
}

/** The live rules as routes, for App.tsx. */
export function redirectRoutes() {
  return REDIRECTS.filter((r) => r.active).map((r) => (
    <Route key={r.from} path={r.from} element={<RedirectTo rule={r} />} />
  ))
}
